/**
 * "Where should we send your receipt?" — for a signed-in buyer who has no email.
 *
 * Presentational: every call lives in `useEmailCapture`, which owns the two
 * stages. This renders whichever one is current and nothing else.
 *
 * Its own class block rather than the guest field's: `guest-email-section` is a
 * bare marker with no rule of its own (the guest path is styled by the
 * `FormInput` inside it), and `.link-button` is written for anchors in the
 * account and post areas. Borrowing either would have put unstyled browser
 * defaults in the middle of checkout.
 */
import { useEffect } from 'react';
import type { useEmailCapture } from './useEmailCapture';
import shared from '../../data/js/shared';

export interface EmailCaptureProps {
  capture: ReturnType<typeof useEmailCapture>;
  /** Surface-specific framing. Defaults to the neutral account wording. */
  heading?: string;
  subheading?: string;
}

type EmailCaptureCopy = (typeof shared)['emailCapture'];

/** The hook's message keys, in the buyer's words. */
const messagesFor = (copy: EmailCaptureCopy): Record<string, string> => ({
  invalid_email: copy.invalidEmail,
  invalid_code: copy.invalidCode,
  too_many_attempts: copy.tooManyCodes,
  send_failed: copy.sendFailed,
  network_error: copy.networkError,
});

export default function EmailCapture({ capture, heading, subheading }: EmailCaptureProps) {
  const copy = shared.emailCapture;
  const { stage, email, setEmail, code, setCode, error, busy, trackShown } = capture;

  // The impression, reported from the one place that knows the panel actually
  // rendered. The caller's gate decides whether this component mounts at all, so
  // mounting IS the impression; the hook cannot tell, because it runs on every
  // render of a page that may never show the panel.
  useEffect(() => {
    trackShown();
  }, [trackShown]);

  if (stage === 'verified') {
    return (
      <div className="email-capture-section" data-testid="email-capture-verified">
        <p className="email-capture-confirmed">{`${copy.verified} ${email}`}</p>
      </div>
    );
  }

  const message = error ? (messagesFor(copy)[error] ?? copy.sendFailed) : null;

  return (
    <div className="email-capture-section" data-testid="email-capture">
      <h3 className="email-capture-heading">{heading ?? copy.heading}</h3>
      <p className="email-capture-subheading">
        {stage === 'email' ? (subheading ?? copy.subheading) : `${copy.codeSubheading} ${email}`}
      </p>

      {stage === 'email' ? (
        <div className="email-capture-controls">
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            className="email-capture-input"
            aria-label={copy.emailLabel}
            placeholder={copy.emailLabel}
            data-testid="email-capture-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            type="button"
            className="email-capture-button"
            data-testid="email-capture-send"
            disabled={busy}
            onClick={() => void capture.requestCode()}
          >
            {busy ? copy.sending : copy.sendCode}
          </button>
        </div>
      ) : (
        <>
          <div className="email-capture-controls">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="email-capture-input"
              aria-label={copy.codeLabel}
              placeholder={copy.codeLabel}
              data-testid="email-capture-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button
              type="button"
              className="email-capture-button"
              data-testid="email-capture-verify"
              disabled={busy}
              onClick={() => void capture.submitCode()}
            >
              {busy ? copy.verifying : copy.verify}
            </button>
          </div>
          <button
            type="button"
            className="email-capture-edit"
            data-testid="email-capture-edit"
            onClick={capture.editEmail}
          >
            {copy.editEmail}
          </button>
        </>
      )}

      {message && (
        <p className="email-capture-error" role="alert" data-testid="email-capture-error">
          {message}
        </p>
      )}
    </div>
  );
}
